/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { FormEvent } from 'react';
import { useEffect, useRef } from 'react';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import SurveyProgressIndicator from 'bundles/expression-of-interest/components/ddp-multistep/SurveyProgressIndicator';
import SurveyQuestionField from 'bundles/expression-of-interest/components/ddp-multistep/SurveyQuestionField';
import type { SurveyFieldConfig } from 'bundles/expression-of-interest/components/ddp-multistep/SurveyQuestionField';

import _t from 'i18n!nls/expression-of-interest';

type Props = {
  heading: string;
  submitLabel: string;
  fields: SurveyFieldConfig[];
  values: Record<string, string>;
  onFieldChange: (fieldId: string, value: string) => void;
  onSubmit: () => void;
  stepNumber: number;
  totalSteps: number;
  focusHeadingOnMount?: boolean;
  fieldsOptional?: boolean;
};

const styles = {
  heading: css`
    margin-bottom: 0;
  `,
  grid: css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--cds-spacing-150) var(--cds-spacing-200);
    margin: var(--cds-spacing-200) 0;

    ${breakpoints.down('xs')} {
      grid-template-columns: 1fr;
    }
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
    flex-wrap: nowrap;

    ${breakpoints.down('xs')} {
      flex-wrap: wrap;
    }
  `,
  caption: css`
    margin: 0;
  `,
  submitButton: css`
    white-space: nowrap;
    flex-shrink: 0;
  `,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PHONE_DIGITS = 7;
const MAX_PHONE_DIGITS = 15;

const isFieldComplete = (field: SurveyFieldConfig, values: Record<string, string>) => {
  const value = values[field.id]?.trim();
  if (!value) {
    return false;
  }
  if (field.kind === 'email') {
    return EMAIL_PATTERN.test(value);
  }
  if (field.kind === 'tel') {
    const digitCount = value.replace(/\D/g, '').length;
    return digitCount >= MIN_PHONE_DIGITS && digitCount <= MAX_PHONE_DIGITS;
  }
  return true;
};

const SurveySectionForm = (props: Props) => {
  const {
    heading,
    submitLabel,
    fields,
    values,
    onFieldChange,
    onSubmit,
    stepNumber,
    totalSteps,
    focusHeadingOnMount,
    fieldsOptional,
  } = props;

  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (focusHeadingOnMount) {
      headingRef.current?.focus();
    }
  }, [focusHeadingOnMount]);

  const isSectionComplete = fieldsOptional || fields.every((field) => isFieldComplete(field, values));

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isSectionComplete) {
      return;
    }
    onSubmit();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Typography2 component="h3" variant="titleSmall" css={styles.heading} ref={headingRef} tabIndex={-1}>
        {heading}
      </Typography2>
      <SurveyProgressIndicator stepNumber={stepNumber} totalSteps={totalSteps} />
      <div css={styles.grid}>
        {fields.map((field) => {
          if (field.kind === 'dropdown') {
            return (
              <SurveyQuestionField
                key={field.id}
                kind="dropdown"
                label={field.label}
                placeholder={field.placeholder}
                fullWidth={field.fullWidth}
                value={values[field.id] ?? ''}
                onChange={(value) => onFieldChange(field.id, value)}
                options={field.options}
              />
            );
          }

          return (
            <SurveyQuestionField
              key={field.id}
              kind={field.kind}
              label={field.label}
              placeholder={field.placeholder}
              fullWidth={field.fullWidth}
              value={values[field.id] ?? ''}
              onChange={(value) => onFieldChange(field.id, value)}
            />
          );
        })}
      </div>
      <div css={styles.actions}>
        <Button
          variant="primary"
          icon={<ArrowNextIcon />}
          iconPosition="after"
          type="submit"
          disabled={!isSectionComplete}
          css={styles.submitButton}
        >
          {submitLabel}
        </Button>
        <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.caption}>
          {_t('Takes less than a minute. No commitment required.')}
        </Typography2>
      </div>
    </form>
  );
};

export default SurveySectionForm;
